import {
  Form,
  Select,
  InputNumber,
  ConfigProvider,
  Col,
  Row,
  Spin,
} from "antd";
import { formatTranslation } from "../../../../../../../shared/utils/translationUtils";
import { DropdownEmptyState } from "../../../../../../../shared/layout/DropdownEmptyState";
import { useTranslation } from "react-i18next";
import { type ReactElement, useMemo } from "react";
import { getProductUnit, restrictToTwoDecimals } from "../../../../../utils";

interface ProductRowProps {
  index: number;
  product: {
    value?: string;
    rate?: number;
    measure_unit?: string;
    variety?: string;
  } | null;
  productOptions: Array<{ label: string; value: string }>;
  loading: boolean;
  isLoading: boolean;
  formData: any;
  handleChangeFormData: (data: any, refresh?: boolean) => void;
  pIndex: number;
  handleSearch: (value: string) => void;
  searchLoading: boolean;
  length?: number;
}

export const ProductRow = ({
  index,
  product,
  formData,
  productOptions,
  loading,
  isLoading,
  handleChangeFormData,
  handleSearch,
  searchLoading,
  pIndex,
  length = 0,
}: ProductRowProps): ReactElement => {
  const mergedProductOptions = useMemo(() => {
    if (!product?.value) return productOptions;
    const exists = productOptions.some((opt) => opt.value === product.value);
    if (exists) return productOptions;
    return [
      {
        label: (
          <div className="bio-product-option-label">
            <div className="product-name">{product?.variety}</div>
            <div className="product-indication">{"-"}</div>
          </div>
        ),
        value: product?.value,
        key: product?.value,
        disabled: false,
        variety: product?.variety,
        measure_unit: product?.measure_unit,
      },
      ...productOptions,
    ];
  }, [product, productOptions]);

  const { t } = useTranslation();
  function handleRateChange(
    pIndex: number,
    index: number,
    rate: number | null
  ): void {
    const updatedProducts = [...formData[pIndex].products];
    updatedProducts[index].rate = rate;
    formData[pIndex].products = [...updatedProducts];
    handleChangeFormData([...formData]);
  }
  function handleChangeProduct(
    pIndex: number,
    index: number,
    value: string
  ): void {
    const curProduct = productOptions.find(
      (product: any) => product.value === value
    );
    const updatedProducts = [...formData[pIndex].products];
    updatedProducts[index] = { ...curProduct, rate: 0 };
    formData[pIndex].products = [...updatedProducts];
    handleChangeFormData([...formData], true);
  }
  function handleDeleteProduct(pIndex: number, index: number): void {
    const updatedProducts = [...formData[pIndex].products];
    updatedProducts.splice(index, 1);
    formData[pIndex].products = updatedProducts;
    handleChangeFormData([...formData]);
  }

  return (
    <Row gutter={16} className="product-row">
      <Col span={16}>
        <Form>
          <ConfigProvider
            renderEmpty={() => <DropdownEmptyState isloading={isLoading} />}
          >
            <Form.Item
              label={
                index === 0
                  ? formatTranslation(
                      t(
                        "createProtocol.products.biologicals.product.chooseProductLabel"
                      )
                    )
                  : ""
              }
              required
              rules={[
                {
                  required: true,
                  message: formatTranslation(
                    t("createProtocol.products.biologicals.product.error")
                  ),
                },
              ]}
            >
              <Select
                loading={searchLoading}
                placeholder={formatTranslation(
                  t(
                    "createProtocol.products.biologicals.product.chooseProductPlaceholder"
                  )
                )}
                value={product?.value ?? null}
                onChange={(value: string) => {
                  handleChangeProduct(pIndex, index, value);
                }}
                showSearch
                allowClear
                filterOption={false}
                onSearch={handleSearch}
                options={searchLoading ? [] : mergedProductOptions}
                size="large"
                notFoundContent={
                  searchLoading ? (
                    <div
                      style={{
                        height: "200px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Spin size="small" />
                    </div>
                  ) : undefined
                }
              />
            </Form.Item>
          </ConfigProvider>
        </Form>
      </Col>
      <Col span={6}>
        <Form>
          <Form.Item
            label={
              index === 0
                ? formatTranslation(
                    t("createProtocol.products.biologicals.product.rateLabel")
                  )
                : ""
            }
            required
            rules={[
              {
                required: true,
                message: formatTranslation(
                  t(
                    "createProtocol.products.biologicals.product.rateErrorLabel"
                  )
                ),
              },
            ]}
          >
            <InputNumber
              placeholder="0"
              min={0}
              step={0.01}
              precision={2}
              value={product?.rate ?? ""}
              disabled={!product || loading}
              onChange={(rate: any) => {
                handleRateChange(pIndex, index, rate);
              }}
              onFocus={() => {
                if (product?.rate === 0) {
                  handleRateChange(pIndex, index, null);
                }
              }}
              onKeyDown={restrictToTwoDecimals}
              size="large"
              addonAfter={
                <div
                  style={{
                    width: "26px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <span style={{ fontSize: "12px" }}>
                    {getProductUnit(product?.measure_unit)}
                  </span>
                </div>
              }
              className="product-rate-input"
            />
            {/* <InputNumber
              type="number"
              value={product?.rate ?? ""}
              disabled={!product?.value || loading}
              onChange={(rate: any) => {handleRateChange(pIndex, index, rate)}}
              size="large"
              className="product-rate-input"
            /> */}
          </Form.Item>
        </Form>
      </Col>
      {length > 1 && (
        <Col span={2}>
          <div style={{ position: "relative" }}>
            <div
              style={{
                position: "absolute",
                top: index === 0 ? "42px" : "10px",
                left: "15px",
                cursor: "pointer",
              }}
              onClick={() => {
                handleDeleteProduct(pIndex, index);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="18"
                viewBox="0 0 14 18"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.5 0H4.5L3.5 1H0V3H14V1H10.5L9.5 0ZM11 6V16H3V6H11ZM1 4H13V16C13 17.1 12.1 18 11 18H3C1.9 18 1 17.1 1 16V4Z"
                  fill="#EB4B4B"
                />
              </svg>
            </div>
          </div>
        </Col>
      )}
    </Row>
  );
};
